import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { CURRENT_SEASON } from '@/lib/season';
import { FINISHED_STATUSES } from '@/lib/fixture-status';

/**
 * Returns the "current" matchday — the matchday of the next upcoming fixture.
 *
 * Simple algorithm:
 *  1. Find the non-finished fixture with the earliest kickoff_time.
 *     Return its matchday.
 *  2. Fallback: all fixtures finished — return the highest matchday.
 */
export const useCurrentMatchday = (season: string = CURRENT_SEASON, competitionId?: number) => {
  return useQuery({
    queryKey: ['current-matchday', season, competitionId],
    enabled: competitionId != null,
    queryFn: async () => {
      // 1. Find the matchday of the earliest non-finished fixture.
      let nextFixtureQuery = supabase
        .from('fixtures')
        .select('matchday')
        .eq('season', season)
        .not('status', 'in', `(${FINISHED_STATUSES.join(',')})`)
        .order('kickoff_time', { ascending: true })
        .limit(1);
      if (competitionId != null) nextFixtureQuery = nextFixtureQuery.eq('competition_id', competitionId);

      const { data: nextFixture, error: nextError } = await nextFixtureQuery;

      if (nextError) {
        console.error('Error fetching next fixture:', nextError);
        throw nextError;
      }

      if (nextFixture && nextFixture.length > 0) {
        return nextFixture[0].matchday;
      }

      // 2. Fallback: all fixtures finished — return the highest matchday.
      let latestQuery = supabase
        .from('fixtures')
        .select('matchday')
        .eq('season', season)
        .order('matchday', { ascending: false })
        .limit(1);
      if (competitionId != null) latestQuery = latestQuery.eq('competition_id', competitionId);

      const { data: latest, error: latestError } = await latestQuery;

      if (latestError) {
        console.error('Error fetching latest matchday:', latestError);
        throw latestError;
      }

      if (latest && latest.length > 0) {
        return latest[0].matchday;
      }

      // No fixtures at all
      return null;
    },
    staleTime: 30 * 1000, // refresh more frequently to catch deadline transitions
    refetchOnMount: 'always',
    refetchOnWindowFocus: true,
    refetchInterval: 60 * 1000, // poll every minute so deadline transitions are caught automatically
  });
};
